import type { Challenge } from '@jemmi/schemas';
import { Camera, Gem, Trash2, Upload, Video } from 'lucide-react';
import { useRef, useState } from 'react';

import { ApiError } from '@/lib/apiClient';
import { useAuth } from '@/lib/auth';
import { uploadMedia } from '@/lib/media';
import { useChallengeSubmissions, useCreateSubmission, useDeleteSubmission, useMySubmission, useReplaceSubmission } from '@/lib/submissions';
import { useGiveJem, useRemoveJem } from '@/lib/votes';

type Props = { challenge: Challenge & { status?: 'active' | 'ended'; winnerSubmissionId?: string | null } };

export function SubmissionPanel({ challenge }: Props) {
  const { user } = useAuth();
  const signedIn = Boolean(user);
  const active = challenge.status !== 'ended' && new Date(challenge.endsAt).getTime() > Date.now();
  const isCreator = user?.id === challenge.creatorId;
  const submissions = useChallengeSubmissions(challenge.id);
  const mine = useMySubmission(challenge.id, signedIn);
  const createSubmission = useCreateSubmission();
  const replaceSubmission = useReplaceSubmission(challenge.id);
  const deleteSubmission = useDeleteSubmission(challenge.id);
  const giveJem = useGiveJem(challenge.id);
  const removeJem = useRemoveJem(challenge.id);
  const fileInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  const videoInput = useRef<HTMLInputElement>(null);
  const [caption, setCaption] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mySubmission = mine.data?.submission ?? null;
  const items = submissions.data?.items ?? [];

  const submitFile = async (file?: File) => {
    if (!file || !user || !active || isCreator || busy) return;
    setBusy(true);
    setError(null);
    try {
      const uploaded = await uploadMedia(file, challenge.id);
      if (mySubmission) {
        await replaceSubmission.mutateAsync({
          id: mySubmission.id,
          input: { mediaUrl: uploaded.mediaUrl, mediaType: uploaded.mediaType, caption: caption.trim() || null },
        });
      } else {
        await createSubmission.mutateAsync({
          challengeId: challenge.id,
          mediaUrl: uploaded.mediaUrl,
          mediaType: uploaded.mediaType,
          caption: caption.trim() || null,
        });
      }
      setCaption('');
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : 'Unable to submit media.');
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
      if (cameraInput.current) cameraInput.current.value = '';
      if (videoInput.current) videoInput.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (!mySubmission || !window.confirm('Delete your submission? This cannot be undone.')) return;
    try { await deleteSubmission.mutateAsync(mySubmission.id); } catch (err) { setError(err instanceof Error ? err.message : 'Unable to delete submission.'); }
  };

  const handleVote = async (submissionId: string, hasVoted: boolean) => {
    setError(null);
    try {
      if (hasVoted) await removeJem.mutateAsync();
      else await giveJem.mutateAsync(submissionId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update your Jem.');
    }
  };

  return (
    <section>
      {signedIn && active && !isCreator && (
        <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="font-bold text-slate-900">{mySubmission ? 'Replace your submission' : 'Submit your entry'}</h2>
              <p className="mt-1 text-sm text-slate-500">Photos are automatically optimized to a maximum 2048 px edge. Videos must be 60 seconds or shorter and 50 MB or smaller.</p>
            </div>
            {mySubmission && <button type="button" onClick={handleDelete} className="flex items-center gap-1 text-sm font-semibold text-red-600"><Trash2 className="h-4 w-4" /> Delete entry</button>}
          </div>
          <textarea value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={280} placeholder="Optional caption" className="mt-4 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" disabled={busy} onClick={() => fileInput.current?.click()} className="flex items-center gap-2 rounded-full bg-brand-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"><Upload className="h-4 w-4" /> Choose from device</button>
            <button type="button" disabled={busy} onClick={() => cameraInput.current?.click()} className="flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50"><Camera className="h-4 w-4" /> Take photo</button>
            <button type="button" disabled={busy} onClick={() => videoInput.current?.click()} className="flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50"><Video className="h-4 w-4" /> Record video</button>
            {busy && <span className="self-center text-sm text-slate-500">Preparing and uploading…</span>}
          </div>
          <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp,video/mp4,video/webm,video/quicktime" className="hidden" onChange={(e) => void submitFile(e.target.files?.[0])} />
          <input ref={cameraInput} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => void submitFile(e.target.files?.[0])} />
          <input ref={videoInput} type="file" accept="video/*" capture="environment" className="hidden" onChange={(e) => void submitFile(e.target.files?.[0])} />
          {mySubmission?.status === 'pending_moderation' && <p className="mt-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">Your entry is uploaded and waiting for moderation. It will appear publicly after approval.</p>}
        </div>
      )}

      {!signedIn && active && <p className="mb-6 rounded-xl bg-slate-50 p-4 text-sm text-slate-600">Sign in to submit an entry or give a Jem.</p>}
      {isCreator && active && <p className="mb-6 rounded-xl bg-slate-50 p-4 text-sm text-slate-600">As the challenge creator, you can manage this challenge but cannot submit an entry.</p>}
      {error && <div role="alert" className="mb-5 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}

      <div className="mb-4 flex items-center justify-between"><h2 className="text-xl font-bold text-slate-900">{items.length} {items.length === 1 ? 'Submission' : 'Submissions'}</h2></div>
      {submissions.isLoading ? <p className="py-8 text-center text-slate-500">Loading submissions…</p> : items.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-200 px-6 py-12 text-center"><Gem className="mx-auto mb-3 h-8 w-8 text-slate-400" /><p className="font-medium text-slate-700">No approved submissions yet.</p></div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((submission) => {
            const own = user?.id === submission.userId;
            const winner = challenge.winnerSubmissionId === submission.id;
            return (
              <article key={submission.id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                {submission.mediaType === 'image' ? <img src={submission.mediaUrl} alt={submission.caption ?? `Submission by ${submission.user.displayName}`} loading="lazy" className="aspect-square w-full object-cover" /> : <video src={submission.mediaUrl} controls preload="metadata" className="aspect-square w-full bg-black object-contain" />}
                <div className="p-4">
                  <div className="flex items-center justify-between gap-2"><p className="font-semibold text-slate-900">{submission.user.displayName}</p>{winner && <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-bold text-amber-800">Winner</span>}</div>
                  {submission.caption && <p className="mt-2 text-sm text-slate-600">{submission.caption}</p>}
                  <button type="button" disabled={!signedIn || !active || own || giveJem.isPending || removeJem.isPending} onClick={() => void handleVote(submission.id, submission.hasVoted)} className={`mt-4 flex w-full items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-bold ${submission.hasVoted ? 'bg-brand-600 text-white' : 'border border-brand-200 text-brand-700'} disabled:cursor-not-allowed disabled:opacity-50`}>
                    <Gem className="h-4 w-4" /> {submission.hasVoted ? 'Jem given' : 'Give Jem'} · {submission.jemCount}
                  </button>
                  {own && <p className="mt-2 text-center text-xs text-slate-400">You cannot Jem your own entry.</p>}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
